import { lazy, Suspense } from 'react';
import { RouteProvider, useRoute } from './contexts/RouteContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PartnerSessionProvider } from './contexts/PartnerSessionContext';
import AppShell from './components/layout/AppShell';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const PortalPage = lazy(() => import('./pages/PortalPage'));
const ActivatePage = lazy(() => import('./pages/ActivatePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ResendActivationPage = lazy(() => import('./pages/ResendActivationPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));

function LoadingFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <span
        aria-hidden="true"
        className="h-8 w-8 animate-spin rounded-full border-2 border-brand-blue border-t-transparent"
      />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/**
 * Route resolution for the Partner Onboarding application.
 *
 * M4-R20A — LEGACY AUTH RETIRED. The custom opaque-token invitation flow
 * (`/invite`, `?token=`) and the custom access-session expiry view are
 * superseded by Supabase Auth. Any lingering `/invite` or `?token=` URL is
 * redirected to `/login` (returning login) so users enter the new auth flow.
 *
 * - Landing page at `/` for general visitors.
 * - Account activation at `/activate` (from the app-side activation email).
 * - Returning-partner auth at `/login`, `/forgot-password`, `/reset-password`.
 * - Resend activation at `/resend-activation` (partner-facing, invitation-only).
 * - Account management at `/account`.
 * - Protected portal when an active Supabase Auth session exists.
 */
function AppContent() {
  const { pathname } = useRoute();
  const { status: authStatus, session: authSession } = useAuth();

  let page: React.ReactNode;

  // Explicit auth routes take precedence.
  if (pathname === '/activate') {
    page = <ActivatePage />;
  } else if (pathname === '/login') {
    page = <LoginPage />;
  } else if (pathname === '/forgot-password') {
    page = <ForgotPasswordPage />;
  } else if (pathname === '/reset-password') {
    page = <ResetPasswordPage />;
  } else if (pathname === '/resend-activation') {
    page = <ResendActivationPage />;
  } else if (pathname === '/account') {
    page = <AccountPage />;
  } else if (authStatus === 'loading') {
    page = <LoadingFallback />;
  } else if (authSession) {
    // Authenticated Supabase session — portal is gated on this.
    page = <PortalPage />;
  } else if (pathname === '/invite' || new URLSearchParams(window.location.search).has('token')) {
    // M4-R20A: Legacy invitation flow retired — direct to returning login.
    page = <LoginPage />;
  } else {
    page = <LandingPage />;
  }

  return (
    <AppShell>
      <Suspense fallback={<LoadingFallback />}>{page}</Suspense>
    </AppShell>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RouteProvider>
        <PartnerSessionProvider>
          <AppContent />
        </PartnerSessionProvider>
      </RouteProvider>
    </AuthProvider>
  );
}
